import React, { useState } from "react";
import ProfileCard from "../../common/profileCard";
import StudentSidebar from "../../common/studentSidebar";
import SettingsLinks from "../settingsLinks/settingsLinks";
import SettingsModal from "../settingsModal/settingsModal";
import { useDispatch, useSelector } from "react-redux";
import { StudentDocuments as StudentDocumentsType } from "../../../../core/types/types";
import { updateUser } from "../../../../core/redux/authSlice";
import apiInstance from "../../../../utility/apiInstance";

const DOC_TYPES: { key: keyof StudentDocumentsType; label: string }[] = [
    { key: "passport", label: "Passport Document" },
    { key: "ielts", label: "IELTS Document" },
    { key: "tenth", label: "10th Document" },
    { key: "twelfth", label: "12th Document" },
    { key: "bachelorDmc", label: "Bachelor DMC Document" },
    { key: "backlog", label: "Backlog Document" },
    { key: "degree", label: "Degree Document" },
    { key: "experience", label: "Experience Document" },
    { key: "mediumOfInstruction", label: "Medium of Instruction Document" },
    { key: "letterOfRecommendation", label: "Letter of Recommendation Document" },
    { key: "cv", label: "CV Document" },
    { key: "diploma", label: "Diploma Document" },
    { key: "studyPermit", label: "Study Permit Document" },
    { key: "other", label: "Other Document" },
];

const StudentDocuments = () => {
    const user = useSelector((state: any) => state.auth.user);
    const dispatch = useDispatch();

    const [documents, setDocuments] = useState<StudentDocumentsType>(
        user?.documents ?? {}
    );
    const [loadingKey, setLoadingKey] = useState<keyof StudentDocumentsType | null>(null);
    const [msg, setMsg] = useState({ error: false, text: "" });

    const ALLOWED_TYPES = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ];
    const ALLOWED_EXTENSIONS = /\.(pdf|doc|docx|xls|xlsx)$/i;
    const ALLOWED_LABEL = "PDF, DOC, DOCX, XLS, XLSX";

    const uploadFile = async (key: keyof StudentDocumentsType, file: File) => {
        const typeOk = ALLOWED_TYPES.includes(file.type) || ALLOWED_EXTENSIONS.test(file.name);
        if (!typeOk) {
            setMsg({ error: true, text: `Invalid file type. Allowed: ${ALLOWED_LABEL}.` });
            return;
        }
        setLoadingKey(key);
        setMsg({ error: false, text: "" });

        try {
            const fileData = new FormData();
            fileData.append("file", file);

            const uploadRes = await apiInstance.post("/api/media", fileData, {
                headers: { "Content-Type": "multipart/form-data" },
            });

            const uploadedDoc = uploadRes.data.doc;
            const updated = { ...documents, [key]: uploadedDoc };

            const patchRes = await apiInstance.patch(`/api/users/${user.id}`, {
                documents: updated,
            });

            const savedDocs = patchRes.data.doc?.documents ?? updated;
            setDocuments(savedDocs);
            dispatch(updateUser(patchRes.data.doc));
        } catch (error: any) {
            setMsg({ error: true, text: error.message });
        } finally {
            setLoadingKey(null);
        }
    };

    const deleteFile = async (key: keyof StudentDocumentsType) => {
        const doc = documents[key];
        if (!doc) return;

        setLoadingKey(key);
        setMsg({ error: false, text: "" });

        try {
            const updated = { ...documents, [key]: null };

            const patchRes = await apiInstance.patch(`/api/users/${user.id}`, {
                documents: updated,
            });

            await apiInstance.delete(`/api/media/${doc.id}`);

            const savedDocs = patchRes.data.doc?.documents ?? updated;
            setDocuments(savedDocs);
            dispatch(updateUser(patchRes.data.doc));
        } catch (error: any) {
            setMsg({ error: true, text: error.message });
        } finally {
            setLoadingKey(null);
        }
    };

    const handleDownload = async (doc: any) => {
        try {
            const response = await apiInstance.get(doc?.url, {
                responseType: "blob",
            });

            const url = window.URL.createObjectURL(response.data);
            const link = document.createElement("a");
            link.href = url;
            link.download = doc?.filename || "download";
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error("Download failed", err);
        }
    };

    return (
        <>
            <div className="content">
                <div className="container">
                    <ProfileCard />
                    <div className="row">
                        <StudentSidebar />
                        <div className="col-lg-9">
                            <div className="mb-3">
                                <h5>Settings</h5>
                            </div>
                            <SettingsLinks />
                            <div className="card">
                                <div className="card-body">
                                    <div className="edit-profile-info mb-3">
                                        <h5 className="mb-1">Documents</h5>
                                    </div>
                                    <div className="row">
                                        {DOC_TYPES.map(({ key, label }) => {
                                            const doc = documents[key];
                                            const isLoading = loadingKey === key;

                                            return (
                                                <div className="col-md-4 mb-4" key={key}>
                                                    <label className="form-label fw-semibold">
                                                        {label}
                                                    </label>
                                                    {doc ? (
                                                        <div className="d-flex align-items-center gap-2 border rounded p-2">
                                                            <span
                                                                className="text-truncate flex-grow-1 fs-14"
                                                                title={doc.filename}
                                                            >
                                                                {doc.filename}
                                                            </span>
                                                            <i
                                                                className="cursor-pointer isax isax-import text-primary"
                                                                title="Download"
                                                                onClick={() => handleDownload(doc)}
                                                            />
                                                            <i
                                                                className={`cursor-pointer isax isax-trash text-danger${isLoading ? " opacity-50 pe-none" : ""}`}
                                                                title="Delete"
                                                                onClick={() => {
                                                                    if (!isLoading) deleteFile(key);
                                                                }}
                                                            />
                                                        </div>
                                                    ) : (
                                                        <input
                                                            type="file"
                                                            className="form-control"
                                                            disabled={isLoading}
                                                            accept=".pdf,.doc,.docx,.xls,.xlsx"
                                                            onChange={(e) => {
                                                                const file = e.target.files?.[0];
                                                                if (file) uploadFile(key, file);
                                                                e.target.value = "";
                                                            }}
                                                        />
                                                    )}
                                                    {isLoading && (
                                                        <small className="text-muted">
                                                            Processing...
                                                        </small>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                    {msg.text && (
                                        <span
                                            className={`mt-2 fs-14 fw-normal d-flex align-items-center justify-content-center${msg.error ? " text-danger" : ""}`}
                                        >
                                            {msg.text}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <SettingsModal />
        </>
    );
};

export default StudentDocuments;
